"use client"

import { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  Progress,
} from "reactstrap"
import { Btn } from "../../../AbstractElements"
import { RoleService } from "../../../Service/role-management-service"
import type { Role, RoleStats } from "./types"
import { toast } from "react-toastify"

interface RoleDetailModalProps {
  isOpen: boolean
  onClose: () => void
  role: Role | null
}

export default function RoleDetailModal({ isOpen, onClose, role }: RoleDetailModalProps) {
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<RoleStats | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Fetch role statistics
  useEffect(() => {
    if (isOpen && role) {
      fetchRoleStats()
    }
  }, [isOpen, role])

  const fetchRoleStats = async () => {
    if (!role) return

    setLoading(true)
    setError(null)

    try {
      const roleStats = await RoleService.getStats(role.id)
      setStats(roleStats)
    } catch (error: any) {
      console.error("Failed to fetch role stats:", error)
      setError("Failed to load role statistics")
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusColor = (isActive: boolean) => {
    return isActive ? "success" : "secondary"
  }

  const getStatusText = (isActive: boolean) => {
    return isActive ? "Active" : "Inactive"
  }

  if (!role) return null

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        <div className="d-flex align-items-center">
          <span className="me-2">Role Details</span>
          <Badge color={getStatusColor(role.is_active)} pill>
            {getStatusText(role.is_active)}
          </Badge>
        </div>
      </ModalHeader>

      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading role details...</div>
          </div>
        ) : (
          <>
            {/* Basic Information */}
            <Card className="mb-3">
              <CardHeader>
                <CardTitle tag="h6" className="mb-0">
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <label className="form-label fw-bold text-muted small">NAME</label>
                      <div className="fs-5">{role.name}</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold text-muted small">STATUS</label>
                      <div>
                        <Badge color={getStatusColor(role.is_active)} pill className="px-3 py-2">
                          {getStatusText(role.is_active)}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <label className="form-label fw-bold text-muted small">CREATED</label>
                      <div>{formatDate(role.created_at)}</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold text-muted small">LAST UPDATED</label>
                      <div>{formatDate(role.updated_at)}</div>
                    </div>
                  </Col>
                </Row>

                {role.description && (
                  <div className="mb-0">
                    <label className="form-label fw-bold text-muted small">DESCRIPTION</label>
                    <div className="p-3 bg-primary rounded">{role.description}</div>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Statistics */}
            {stats && (
              <Card className="mb-3">
                <CardHeader>
                  <CardTitle tag="h6" className="mb-0">
                    Usage Statistics
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label fw-bold text-muted small mb-0">ASSIGNED MENUS</label>
                          <Badge color="info" pill>
                            {stats.menu_count}
                          </Badge>
                        </div>
                        <Progress value={Math.min((stats.menu_count / 20) * 100, 100)} color="info" className="mb-1" />
                        <small className="text-muted">{stats.menu_count} menus assigned</small>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label fw-bold text-muted small mb-0">ASSIGNED USERS</label>
                          <Badge color="primary" pill>
                            {stats.user_count}
                          </Badge>
                        </div>
                        <Progress
                          value={Math.min((stats.user_count / 50) * 100, 100)}
                          color="primary"
                          className="mb-1"
                        />
                        <small className="text-muted">
                          {stats.active_users} active, {stats.user_count - stats.active_users} inactive
                        </small>
                      </div>
                    </Col>
                  </Row>

                  {stats.recent_assignments > 0 && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <small className="text-muted">
                        <i className="fa fa-info-circle me-1"></i>
                        {stats.recent_assignments} recent assignment{stats.recent_assignments !== 1 ? "s" : ""} in the
                        last 30 days
                      </small>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle tag="h6" className="mb-0">
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-wrap gap-2">
                  <Btn
                    color="primary"
                    size="sm"
                    onClick={() => {
                      onClose()
                      // Navigate to role menu management
                      window.location.href = `/role-management/${role.id}/menus`
                    }}
                  >
                    <i className="fa fa-list me-1"></i>
                    Manage Menus
                  </Btn>
                  {/* <Btn
                    color="info"
                    size="sm"
                    onClick={() => {
                      onClose()
                      // Navigate to user role management
                      window.location.href = `/user-roles?role=${role.id}`
                    }}
                  >
                    <i className="fa fa-users me-1"></i>
                    View Users
                  </Btn> */}
                  <Btn
                    color={role.is_active ? "warning" : "success"}
                    size="sm"
                    onClick={async () => {
                      try {
                        await RoleService.toggleStatus(role.id)
                        toast.success(`Role ${role.is_active ? "deactivated" : "activated"} successfully`)
                        onClose()
                      } catch (error) {
                        toast.error("Failed to update role status")
                      }
                    }}
                  >
                    <i className={`fa fa-toggle-${role.is_active ? "off" : "on"} me-1`}></i>
                    {role.is_active ? "Deactivate" : "Activate"}
                  </Btn>
                </div>
              </CardBody>
            </Card>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Btn color="secondary" onClick={onClose}>
          Close
        </Btn>
      </ModalFooter>
    </Modal>
  )
}
